// Coalesce pointer snapshots before the socket send. Keep the newest update, space updates,
// and let a clear retire the stream even while an ability use is in flight.

export type PreviewPostAction = 'update' | 'clear';
export type PreviewPostClock = { now(): number; setTimer(run: () => void, ms: number): unknown; clearTimer(timer: unknown): void };

const pageClock: PreviewPostClock = {
    now: () => Date.now(),
    setTimer: (run, ms) => window.setTimeout(run, ms),
    clearTimer: timer => window.clearTimeout(timer as number),
};

/** Shared with every bundle copy by janus-engine/pl/background.ts: the count of ability uses in flight. */
export function abilityUseInFlight(): boolean {
    return ((globalThis as { __ftJanusBackground?: { actions?: number } }).__ftJanusBackground?.actions ?? 0) > 0;
}

/** `send` settles when the transport attempt completes. */
export function createPreviewPostQueue(send: (body: string) => Promise<unknown>, busy: () => boolean = abilityUseInFlight,
    clock: PreviewPostClock = pageClock, spacingMs = 400) {
    let pending: { action: PreviewPostAction; body: string } | null = null;
    let timer: unknown = null, lastSentAt = -Infinity;
    const inFlight = { update: 0, clear: 0 };
    const counts = { requested: 0, sent: 0, coalesced: 0 };
    function pump(): void {
        const next = pending;
        if (!next) return;
        const clearing = next.action === 'clear';
        if (timer !== null) {
            if (!clearing) return;
            clock.clearTimer(timer); timer = null;
        }
        if (inFlight[next.action]) return;
        const wait = clearing ? 0 : busy() ? spacingMs : lastSentAt + spacingMs - clock.now();
        if (wait > 0) { timer = clock.setTimer(() => { timer = null; pump(); }, wait); return; }
        pending = null; counts.sent++; inFlight[next.action]++;
        if (!clearing) lastSentAt = clock.now();
        // A failed post is not retried: the next stage change or heartbeat carries the current wind-up.
        void send(next.body).catch(() => undefined).then(() => { inFlight[next.action]--; pump(); });
    }
    return {
        post(action: PreviewPostAction, body: string): void {
            counts.requested++;
            if (pending) counts.coalesced++;
            pending = { action, body };
            pump();
        },
        stats: () => ({ ...counts, pending: pending?.action ?? null, updatesInFlight: inFlight.update, clearsInFlight: inFlight.clear }),
    };
}
